import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { getCargos } from "../../../../../../../redux/actions/empleador";
import { validarCuilCuit } from "../../../../../../../util/cuilcuit";
import FormikActions from "../../../../../../commons/formik/FormikActions";
import FormikTextField from "../../../../../../commons/formik/FormikTextField";
import CircularLoading from "../../../../../../commons/loading/CircularLoading";
import ResponsablesEnum from "../../../../../../enums/responsablesEnum";

const defaultInitialValues = {
    apellidoNombre: "",
    cuit: "",
    propio: "",
    nroMatricula: "",
    matriculaOtorgadaPor: "",
    horasAsignadas: "",
};

const validationSchema = Yup.object({
    apellidoNombre: Yup.string()
        .max(100, "Máximo 100 caracteres")
        .required("Campo obligatorio"),
    cuit: Yup.string()
        .matches(/^\d{11}$/, "El CUIL/CUIT debe contener 11 dígitos numéricos")
        .required("Campo obligatorio")
        .test({
            name: "cuitValido",
            message: "Debe ingresar un CUIT/CUIL válido",
            test: (value) => (value ? validarCuilCuit(value, true) : false),
        }),
    propio: Yup.string().required("Debe seleccionar Interno o Externo"),
    nroMatricula: Yup.string()
        .matches(/^\d*$/, "Ingrese sólo números")
        .max(50, "Máximo 50 caracteres")
        .required("Campo obligatorio"),
    matriculaOtorgadaPor: Yup.string()
        .max(100, "Máximo 100 caracteres")
        .required("Campo obligatorio"),
    horasAsignadas: Yup.number()
        .required("Campo obligatorio")
        .max(8760, "Máximo 8760 horas")
        .positive("Ingrese números positivos")
        .integer("No ingrese caracteres especiales")
        .typeError("Ingrese sólo números"),
});

const Res81ResponsableHySDialog = ({ open, handleClose, handleSubmit, initialValues: propInitialValues, isEditing, loading = false }) => {
    const dispatch = useDispatch();
    const [cargoIdFromDispatch, setCargoIdFromDispatch] = useState(null);

    useEffect(() => {
        if (open) {
            dispatch(getCargos(ResponsablesEnum.seguridadHigiene))
                .then((res) => {
                    let cargo = res?.value?.data;
                    if (cargo && cargo.length > 0) {
                        setCargoIdFromDispatch(cargo[0].id);
                    }
                })
        }
    }, [dispatch, open]);

    const getInitialValues = () => {
        if (propInitialValues && isEditing) {
            return {
                apellidoNombre: (propInitialValues.nombreCompleto || "").replace(",", "").trim(),
                cuit: (propInitialValues.cuit || "").replace(/[-\s]/g, ""),
                propio: propInitialValues.propio ? "interno" : "externo",
                nroMatricula: propInitialValues.nroMatricula || "",
                matriculaOtorgadaPor: propInitialValues.matriculaOtorgadaPor || "",
                horasAsignadas: propInitialValues.horasMensuales?.toString() || "",
            };
        }
        return defaultInitialValues;
    };

    const onSubmit = (values) => {
        const [apellido, ...nombresArr] = (values.apellidoNombre || "").split(" ");
        handleSubmit({
            cargoEmpresa: cargoIdFromDispatch,
            apellido: apellido || "",
            nombres: nombresArr.join(" ") || "",
            cuit: values.cuit,
            propio: values.propio === "interno",
            nroMatricula: values.nroMatricula,
            matriculaOtorgadaPor: values.matriculaOtorgadaPor,
            horasAsignadas: parseInt(values.horasAsignadas),
        });
    };

    return (
        <Dialog open={open} fullWidth maxWidth="sm">
            <DialogTitle>{isEditing ? "Editar Responsable de Higiene y Seguridad" : "Agregar Responsable de Higiene y Seguridad"}</DialogTitle>
            <Formik
                initialValues={getInitialValues()}
                enableReinitialize
                onSubmit={onSubmit}
                validationSchema={validationSchema}
            >
                {({ values, setFieldValue, errors, touched }) => (
                    <Form>
                        <DialogContent>
                            {loading ? (
                                <CircularLoading />
                            ) : (
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <FormikTextField
                                            name="apellidoNombre"
                                            label="Apellido y Nombre *"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikTextField
                                            name="cuit"
                                            label="CUIL o CUIT *"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormControl
                                            error={touched.propio && Boolean(errors.propio)}
                                            sx={{ flexDirection: "row", alignItems: "center", gap: 2 }}
                                        >
                                            <FormLabel sx={{ mb: 0 }}>Contacto *</FormLabel>
                                            <RadioGroup
                                                row
                                                value={values.propio}
                                                onChange={(e) =>
                                                    setFieldValue("propio", e.target.value)
                                                }
                                            >
                                                <FormControlLabel
                                                    value="interno"
                                                    control={<Radio />}
                                                    label="Interno"
                                                />
                                                <FormControlLabel
                                                    value="externo"
                                                    control={<Radio />}
                                                    label="Externo"
                                                />
                                            </RadioGroup>
                                            {touched.propio && errors.propio && (
                                                <FormHelperText>{errors.propio}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikTextField
                                            name="nroMatricula"
                                            label="Matrícula *"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikTextField
                                            name="matriculaOtorgadaPor"
                                            label="Título Otorgado por *"
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <FormikTextField
                                            name="horasAsignadas"
                                            label="Horas Mensuales Asignadas al Servicio *"
                                            fullWidth
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        </DialogContent>
                        <DialogActions>
                            <FormikActions
                                handleCancel={handleClose}
                                loading={loading}
                            />
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default Res81ResponsableHySDialog;
